/**
 * relayActionItems — pure derivations that turn a flat list of TeamLogMessage
 * entries into the four Action Required sections. No Firestore reads, no
 * React, no side effects. App.tsx owns the message stream, the acknowledged-
 * id set, and passes both in as arguments.
 *
 * Heuristic for "unacknowledged" per pre-flight `IfXyyJg01e1ox2VAIZIU`:
 *
 *   - Awaiting Travis: message has a critical tag (pre-flight | question |
 *     needs-review | blocker), author is NOT travis, and no later message
 *     from travis exists in the stream, and the id isn't in acknowledgedIds.
 *
 *   - Awaiting Agent: symmetric — author is travis, no later message from
 *     any agent exists, id not acknowledged.
 *
 *   - Parked: tagged 'parked', not acknowledged.
 *
 *   - Recently Resolved: the last `RECENTLY_RESOLVED_LIMIT` ids to enter
 *     acknowledgedIds, ordered most-recent-first.
 *
 * The "no later response" check is deliberately conservative — any later
 * message from the expected responder clears the item. This will produce
 * false negatives (agent replied in a different thread — thinks item
 * resolved when it isn't) but keeps the panel tight. Refinement comes
 * with real usage.
 *
 * 15-minute threshold: items older than 15 min with no response are
 * flagged `urgent`. Consumers render a red dot.
 */

import type { TeamLogMessage } from "../types";
import { KNOWN_TAGS } from "./tagColors";

export const CRITICAL_TAGS = ["pre-flight", "question", "needs-review", "blocker"] as const;
export const URGENT_THRESHOLD_MS = 15 * 60 * 1000;
export const RECENTLY_RESOLVED_LIMIT = 5;

// Tightened per Travis feedback (`qt7b2VNwbh4DJJ1L2hTJ`, bug 3): the
// initial heuristic was over-triggering by walking the entire stream
// window (up to 200 messages, typically ~several days). Only surface
// items posted in the last 24 hours — older stuff is stale and belongs
// in the hygiene report, not the live panel.
export const AWAITING_WINDOW_MS = 24 * 60 * 60 * 1000;

// Messages whose ids appear here are treated as if they don't exist when
// deriving Action Required sections. Matches the client-side hide list
// in TeamLogStream (paste contamination that Firestore rules currently
// can't delete). Kept in sync manually until an admin delete path lands.
const HIDDEN_MESSAGE_IDS = new Set<string>([
  "n2ZNv1r2DKnXplFDWnP9",
  "qypITi8tLlamFyCxxPkK",
]);

/**
 * Sprint 6 C4 (EFISSUE-817 Phase 2) — runtime overrides for the
 * Action Required derivations. Workspace doc seeds defaults that
 * mirror the module constants; passing `rules` here threads the live
 * Firestore values into the pure derivation functions without coupling
 * them to React or Firestore.
 *
 * `charterRules.ts` re-exports this shape under the `CharterRules`
 * name; keeping the type defined here avoids a circular import (the
 * charter helper depends on the constants below).
 */
export interface ActionItemRules {
  action_required_tags: string[];
  parked_tags: string[];
  awaiting_window_ms: number;
  urgent_threshold_ms: number;
  agent_authors: string[];
}

const DEFAULT_RULES: ActionItemRules = {
  action_required_tags: [...CRITICAL_TAGS],
  parked_tags: ["parked"],
  awaiting_window_ms: AWAITING_WINDOW_MS,
  urgent_threshold_ms: URGENT_THRESHOLD_MS,
  agent_authors: ["claude-code", "advisor"],
};

export interface ActionItem {
  id: string;
  message: TeamLogMessage;
  /** True when the item has been waiting longer than 15 minutes. */
  urgent: boolean;
  /** Minutes since the message was posted (floored). */
  ageMinutes: number;
}

function ageMs(msg: TeamLogMessage, now: number): number {
  const t = Date.parse(msg.timestamp);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, now - t);
}

function hasCritical(msg: TeamLogMessage, criticalTags: string[]): boolean {
  if (!msg.tags || msg.tags.length === 0) return false;
  return msg.tags.some(t => criticalTags.includes(t));
}

function authorString(msg: TeamLogMessage): string {
  return typeof msg.author === "string" ? msg.author : String(msg.author);
}

/**
 * Messages are assumed to be in ascending timestamp order (App.tsx reverses
 * the Firestore desc result before passing down). A later message means a
 * higher index. The "later response" check scans the tail of the stream
 * once per message id — O(n²) in the worst case but n ≤ 200 in practice,
 * so fine.
 */
function hasLaterResponseFrom(
  messages: TeamLogMessage[],
  startIndex: number,
  matchAuthor: (a: string) => boolean,
): boolean {
  for (let i = startIndex + 1; i < messages.length; i++) {
    if (matchAuthor(authorString(messages[i]))) return true;
  }
  return false;
}

function toItem(msg: TeamLogMessage, now: number, urgentThresholdMs: number): ActionItem {
  const a = ageMs(msg, now);
  return {
    id: msg.id,
    message: msg,
    urgent: a >= urgentThresholdMs,
    ageMinutes: Math.floor(a / 60_000),
  };
}

function isVisible(msg: TeamLogMessage, acknowledgedIds: Set<string>): boolean {
  if (HIDDEN_MESSAGE_IDS.has(msg.id)) return false;
  if (acknowledgedIds.has(msg.id)) return false;
  return true;
}

function withinWindow(msg: TeamLogMessage, now: number, awaitingWindowMs: number): boolean {
  const t = Date.parse(msg.timestamp);
  if (Number.isNaN(t)) return false;
  return now - t <= awaitingWindowMs;
}

export function deriveAwaitingTravis(
  messages: TeamLogMessage[],
  acknowledgedIds: Set<string>,
  now: number = Date.now(),
  rules: ActionItemRules = DEFAULT_RULES,
): ActionItem[] {
  const out: ActionItem[] = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!isVisible(msg, acknowledgedIds)) continue;
    if (!withinWindow(msg, now, rules.awaiting_window_ms)) continue;
    if (!hasCritical(msg, rules.action_required_tags)) continue;
    if (authorString(msg) === "travis") continue;
    if (hasLaterResponseFrom(messages, i, a => a === "travis")) continue;
    out.push(toItem(msg, now, rules.urgent_threshold_ms));
  }
  return out.reverse();
}

export function deriveAwaitingAgent(
  messages: TeamLogMessage[],
  acknowledgedIds: Set<string>,
  now: number = Date.now(),
  rules: ActionItemRules = DEFAULT_RULES,
): ActionItem[] {
  const agentAuthors = new Set(rules.agent_authors);
  const out: ActionItem[] = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!isVisible(msg, acknowledgedIds)) continue;
    if (!withinWindow(msg, now, rules.awaiting_window_ms)) continue;
    if (!hasCritical(msg, rules.action_required_tags)) continue;
    if (authorString(msg) !== "travis") continue;
    if (hasLaterResponseFrom(messages, i, a => agentAuthors.has(a))) continue;
    out.push(toItem(msg, now, rules.urgent_threshold_ms));
  }
  return out.reverse();
}

export function deriveParked(
  messages: TeamLogMessage[],
  acknowledgedIds: Set<string>,
  now: number = Date.now(),
  rules: ActionItemRules = DEFAULT_RULES,
): ActionItem[] {
  const out: ActionItem[] = [];
  for (const msg of messages) {
    if (!isVisible(msg, acknowledgedIds)) continue;
    if (!msg.tags || !rules.parked_tags.some(t => msg.tags!.includes(t))) continue;
    out.push(toItem(msg, now, rules.urgent_threshold_ms));
  }
  return out.reverse();
}

/**
 * Sprint 2, Commit 2 (`FGrrZUydSmB9HyRJ0KkA`): Recently Resolved is now
 * derived directly from the Firestore-backed `acknowledged_by` array
 * plus `acknowledged_at` timestamp. Filter to messages where my UID is
 * in `acknowledged_by`, sort by `acknowledged_at` desc, cap at
 * RECENTLY_RESOLVED_LIMIT. The old localStorage-backed `recentlyResolvedIds`
 * prop goes away; the new shape is a pure query over the message list.
 */
export function deriveRecentlyResolved(
  messages: TeamLogMessage[],
  myUid: string,
  now: number = Date.now(),
  rules: ActionItemRules = DEFAULT_RULES,
): ActionItem[] {
  if (!myUid) return [];
  const acked = messages
    .filter(m => !HIDDEN_MESSAGE_IDS.has(m.id))
    .filter(m => Array.isArray(m.acknowledged_by) && m.acknowledged_by.includes(myUid))
    .sort((a, b) => (b.acknowledged_at ?? 0) - (a.acknowledged_at ?? 0))
    .slice(0, RECENTLY_RESOLVED_LIMIT);
  return acked.map(m => toItem(m, now, rules.urgent_threshold_ms));
}

/**
 * Prefix-match against the hardcoded tag dictionary (`KNOWN_TAGS`). V1
 * has no registry endpoint; this is the entire universe of searchable
 * tags. Empty query returns empty (the input's placeholder prompts the
 * user to start typing). Matching is case-insensitive.
 */
export function matchTags(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [];
  return (KNOWN_TAGS as readonly string[]).filter(t => t.toLowerCase().startsWith(q));
}
